import { HomePageInventoryItem } from '@app/data/clients/inventoryHydrationServiceClient/queries/types';
import { AnalyticsComponentName } from '@app/pages/i/analytics';
import { Box, Button, constants, Text } from '@redbubble/design-system';
import { useIntl } from '@redbubble/next/intl';
import type { UserInfo } from '@redbubble/next/types';
import {
  type Item as AnalyticsEcommerceItem,
  AnalyticsItemsList,
  AnalyticsItemsListIDNameMap,
} from '@redbubble/next-analytics/gtm';
import { useUserInfo } from '@redbubble/next-provider';
import {
  RSPageType,
  RSProductClickedLocation,
} from '@redbubble/next-provider/rudderstack';
import React, { memo, useEffect, useMemo, useRef } from 'react';
import { CarouselSection } from '../../../../components/CarouselSection';
import {
  CarouselSectionContext,
  CarouselSectionContextValue,
} from '../../../../components/CarouselSection/carouselSectionContext';
import { useListsInclusions } from '../../../../hooks/useListsInclusions';
import {
  parseHomePageCarouselItemToAnalyticsEcommerceItem,
  trackGA4ViewItemListFanArtHomePage,
} from '../../analytics';
import {
  FAM_PROPERTIES,
  FAN_ID_ASSASSINS_CREED,
  FAN_ID_BOB_ROSS,
  FAN_ID_DUNE,
  FAN_ID_SCHITTS_CREEK,
  famPropertyDisplayOrder,
} from '../../constants';
import { FanArtCardSkeleton } from './components/FanArtCardSkeleton';
import styles from './FanArtSection.module.scss';
import messages from './messages';
import {
  mapItemsToMediaCardProps,
  mapPriceProps,
  useFanArtProducts,
} from './utils';

const famIdToMessageDefinitionMap = {
  [FAN_ID_DUNE]: messages.carouselTabHeadingDune,
  [FAN_ID_SCHITTS_CREEK]: messages.carouselTabHeadingSchittsCreek,
  [FAN_ID_ASSASSINS_CREED]: messages.carouselTabHeadingAssassinsCreed,
  [FAN_ID_BOB_ROSS]: messages.carouselTabHeadingBobRoss,
};

const defaultFanProperty = famPropertyDisplayOrder[0];

export interface FanArtSectionProps {
  items: HomePageInventoryItem[];
}

export const FanArtSection: React.FC<FanArtSectionProps> = memo(({ items }) => {
  const intl = useIntl();
  const [{ locale, country, currency }] = useUserInfo<UserInfo>();
  const lastLogged = useRef<{
    activeProperty: string;
    items: HomePageInventoryItem[] | null;
  }>({ activeProperty: defaultFanProperty, items: null });
  const {
    fanArtProducts,
    activeProperty,
    loading: fanArtProductsLoading,
    refresh: refreshFanArtProducts,
  } = useFanArtProducts(defaultFanProperty, items);

  const activePropertyLink = intl.formatMessage(messages.shopAllLink, {
    propertyName: intl.formatMessage(
      famIdToMessageDefinitionMap[activeProperty]
    ),
  });

  const topic = intl.formatMessage(famIdToMessageDefinitionMap[activeProperty]);
  const { listsInclusions, refresh: refreshListInclusions } =
    useListsInclusions();

  useEffect(() => {
    if (!fanArtProductsLoading && fanArtProducts) {
      refreshListInclusions(fanArtProducts);
    }
  }, [fanArtProducts, fanArtProductsLoading, refreshListInclusions]);

  useEffect(() => {
    if (
      !fanArtProductsLoading &&
      fanArtProducts &&
      lastLogged.current.activeProperty !== activeProperty &&
      lastLogged.current.items !== fanArtProducts
    ) {
      const analyticsItems = fanArtProducts
        .map((inventoryItem, index) => {
          if (!inventoryItem) return null;
          return parseHomePageCarouselItemToAnalyticsEcommerceItem({
            inventoryItem,
            index,
          });
        })
        .filter(
          (
            item: AnalyticsEcommerceItem | null
          ): item is NonNullable<AnalyticsEcommerceItem> => !!item
        );

      trackGA4ViewItemListFanArtHomePage({
        items: analyticsItems,
        topic,
        listID: AnalyticsItemsList.FanArtCarousel,
        listName: AnalyticsItemsListIDNameMap.fan_art_carousel,
      });
      lastLogged.current = { activeProperty, items: fanArtProducts };
    }
  }, [fanArtProducts, fanArtProductsLoading, activeProperty, topic]);

  const handleTabChange = (event, property) => {
    event.preventDefault();
    refreshFanArtProducts({
      id: property,
      famProperties: [property],
      currency,
      country,
      locale,
    });
  };

  const contextValue: CarouselSectionContextValue = useMemo(() => {
    return {
      mediaCardPropsMapper: mapItemsToMediaCardProps(listsInclusions),
      pricePropsMapper: mapPriceProps,
      analytics: {
        componentName: AnalyticsComponentName.FanArt,
        inViewTracking: () => {
          if (!fanArtProducts) {
            return null;
          }

          const analyticsItems = fanArtProducts
            .map((inventoryItem, index) => {
              if (!inventoryItem) return null;
              return parseHomePageCarouselItemToAnalyticsEcommerceItem({
                inventoryItem,
                index,
              });
            })
            .filter(
              (
                item: AnalyticsEcommerceItem | null
              ): item is NonNullable<AnalyticsEcommerceItem> => !!item
            );

          trackGA4ViewItemListFanArtHomePage({
            items: analyticsItems,
            topic,
            listID: AnalyticsItemsList.FanArtCarousel,
            listName: AnalyticsItemsListIDNameMap.fan_art_carousel,
          });
        },
        listID: AnalyticsItemsList.FanArtCarousel,
      },
      rsLocation: RSProductClickedLocation.FanArtCarousel,
      rsPageType: RSPageType.Home,
    };
  }, [fanArtProducts, listsInclusions, topic]);

  const getSlides = () => {
    if (fanArtProductsLoading || !fanArtProducts) {
      return Array.from({ length: 5 }, (_, index) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: noArrayIndexKey
        <FanArtCardSkeleton key={`fan-art-skeleton-${index}`} />
      ));
    }
    return fanArtProducts?.map((item, index) => {
      return (
        <CarouselSection.CarouselSlide key={`fan-art-slide-${item.id}`}>
          <CarouselSection.CarouselMediaCard
            data={item}
            isLast={index === items.length - 1}
            index={index}
          />
        </CarouselSection.CarouselSlide>
      );
    });
  };

  return (
    <div>
      <Text type="display2" element="h2" block>
        {intl.formatMessage(messages.carouselTitle)}
      </Text>
      <Box
        display="flex"
        justifyContent="space-between"
        marginTop={1}
        marginBottom={2}
      >
        <Box className={styles.tabsContainer} data-testid="fan-art-section">
          {famPropertyDisplayOrder.map((fanPropertyId) => (
            <a
              href={FAM_PROPERTIES[fanPropertyId].viewAllPath}
              className={[
                styles.tab,
                fanPropertyId === activeProperty ? styles.tabSelected : '',
              ]
                .filter(Boolean)
                .join(' ')}
              onClick={(e) => handleTabChange(e, fanPropertyId)}
              key={fanPropertyId}
            >
              {intl.formatMessage(famIdToMessageDefinitionMap[fanPropertyId])}
            </a>
          ))}
        </Box>
        <Box className={styles.seeAllDesktop}>
          <Button
            href={FAM_PROPERTIES[activeProperty].viewAllPath}
            intent={constants.PRIMARY}
          >
            {activePropertyLink}
          </Button>
        </Box>
      </Box>
      <CarouselSectionContext.Provider value={contextValue}>
        <CarouselSection.Container>
          <CarouselSection.Carousel
            itemsInView={{
              mobile: 2,
              tablet: 4,
              desktop: 5,
            }}
          >
            {getSlides()}
          </CarouselSection.Carousel>
        </CarouselSection.Container>
      </CarouselSectionContext.Provider>
      <Box className={styles.seeAllMobile}>
        <Button
          href={FAM_PROPERTIES[activeProperty].viewAllPath}
          intent={constants.PRIMARY}
        >
          {activePropertyLink}
        </Button>
      </Box>
    </div>
  );
});
